import {
  GGetAllStopStatusesQuery,
  GGetBulkItemsQuery,
  GGetCohortsQuery,
  GGetDeliverySheetsQuery,
  GGetDeliveryPhotosQuery,
  GGetFulfillmentDeltasPerRouteQuery,
  GGetItemsPerRouteQuery,
  GGetLocationsQuery,
  GGetOrderDetailsQuery,
  GGetOrdersQuery,
  GGetPackingListQuery,
  GGetPickingLabelDataQuery,
  GGetPrimaryStopStatusesQuery,
  GGetRedeliveryStopDetailsQuery,
  GGetRouteDetailsQuery,
  GGetRouteIssuesQuery,
  GGetRoutesQuery,
  GGetRoutingImportPlanComparisonQuery,
  GGetRoutingImportPlanCutOffQuery,
  GGetScenarioMapStopsQuery,
  GGetStopAddressQuery,
  GGetStopDeliveryDetailsQuery,
  GGetStopDetailsQuery,
  GGetUsersQuery,
  GGetWarehouseDeliveryDetailsQuery,
  GGetWarehouseDeliveryInfoQuery,
  GGetWarehouseDeliveryMapDetailsQuery,
  GGetWarehouseDriverPerformanceDetailsQuery,
  GGetWarehouseOrderCutOffTimeQuery,
  GGetWarehouseRoutingImportPlanComparisonQuery,
  GGetWarehousesQuery,
  GLocationMembershipTier,
  GMoveDeliveryStopsToRouteMutation,
  GRoutingImportPlanOptimizationFailureReason,
  GRoutingImportPlanStatus,
} from './generated';

export type ArrayElement<ArrayType extends unknown[] | undefined> =
  ArrayType extends (infer ElementType)[] ? ElementType : never;

export enum StopType {
  Delivery = 'Delivery',
  PickUp = 'PickUp',
  Redelivery = 'Redelivery',
}

export type UserError = { __typename: string };

export type RouteImportPlan = {
  __typename?: string;
  id: string;
  warehouseId: string;
  status: GRoutingImportPlanStatus;
  optimizationFailureReason?: GRoutingImportPlanOptimizationFailureReason;
  truckCount?: number;
  label?: string;
};

export type RouteImportState = {
  isError: boolean;
  isLoading: boolean;
  isRunning: boolean;
  isUpdateTruckMutationLoading: boolean;
  plan?: RouteImportPlan;
  cohorts?: RoutingImportCohort[];
  shiftTruckCounts?: ShiftTruckCount[];
  isPastWarehouseCutOffTime: boolean;
  scenarios?: RoutificRunSummary[];
  actualRoutesSummary?: ActualRoutesSummary;
  scenariosLoading: boolean;
  scenariosError: boolean;
};

export enum Tense {
  past = 'past',
  present = 'present',
  future = 'future',
}

// GGetWarehousesQuery
export type Warehouse = ArrayElement<GGetWarehousesQuery['warehouses']>;

// GUsersQuery
export type User = ArrayElement<GGetUsersQuery['users']>;

// GGetStopAddressQuery
export type GetStopAddressDelivery = Extract<
  GGetStopAddressQuery['stop'],
  { __typename?: 'Delivery' }
>;
export type GetStopAddressLocation = Extract<
  GetStopAddressDelivery['location'],
  { __typename?: 'Location' }
>;
export type GetStopAddress = GetStopAddressLocation['address'];

export type Location = {
  id?: string;
  __typename?: 'Location';
  membershipTier: GLocationMembershipTier;
  businessName: string;
  address: GeoCoordinates;
  locationDeliveryWindow: { startTime: string; endTime: string };
};

// GGetRoutesQuery
export type Route = ArrayElement<GGetRoutesQuery['routes']>;
export type Stop = ArrayElement<Route['stops']>;
export type Redelivery = ArrayElement<Route['redeliveryStops']>;
export type Delivery = Extract<Stop, { __typename?: 'Delivery' }>;
export type PickUp = Extract<Stop, { __typename?: 'PickUp' }>;
export type Vendor = PickUp['vendor'];
export type GeoCoordinates = Vendor['address'];
export type Driver = Route['driverUser'];
export type HelperDriver = Route['helperDriverUser'];
export type RoutingCohort = Extract<
  Extract<Delivery['location'], { __typename?: 'Location' }>['routingCohort'],
  { __typename?: 'RoutingCohort' }
>;

// GGetStopDetailsQuery
export type GetDropOffDetailsDelivery = Extract<
  GGetStopDetailsQuery['stop'],
  { __typename?: 'Delivery' }
>;
export type GetPickUpDetailsDelivery = Extract<
  GGetStopDetailsQuery['stop'],
  { __typename?: 'PickUp' }
>;
export type Issue =
  | ArrayElement<GetDropOffDetailsDelivery['issues']>
  | ArrayElement<GetPickUpDetailsDelivery['issues']>;
export type Order =
  | ArrayElement<GetDropOffDetailsDelivery['orders']>
  | ArrayElement<GetPickUpDetailsDelivery['orders']>;
export type OrderLine = ArrayElement<
  ArrayElement<GetDropOffDetailsDelivery['orders']>['orderLines']
>;
export type DeliveryOrder = ArrayElement<GetDropOffDetailsDelivery['orders']>;
export type PickUpOrder = ArrayElement<GetPickUpDetailsDelivery['orders']>;
export type GetStopDetailsLocation = Extract<
  GetDropOffDetailsDelivery['location'],
  { __typename?: 'Location' }
>;

// GGetRedeliveryStopDetailsQuery
export type GetRedeliveryStopDetailsStop =
  GGetRedeliveryStopDetailsQuery['redeliveryStop'];
export type RedeliveryOrder = ArrayElement<
  GetRedeliveryStopDetailsStop['orders']
>;

// GMoveDeliveryStopsToRouteMutation
export type DeliveryStopsMoveToRoutePayload =
  GMoveDeliveryStopsToRouteMutation['moveDeliveryStopsToRoute'];

// GGetStopDeliveryDetailsQuery
export type GetStopDeliveryDetailsDelivery = Extract<
  GGetStopDeliveryDetailsQuery['stop'],
  { __typename?: 'Delivery' }
>;
export type Attachment = ArrayElement<
  GetStopDeliveryDetailsDelivery['attachments']
>;

// GGetAllStopStatusesQuery
export type GetStopStatusMyStop = ArrayElement<
  ArrayElement<GGetAllStopStatusesQuery['routes']>['stops']
>;
export type GetStopStatusMyRedeliveryStop = ArrayElement<
  ArrayElement<GGetAllStopStatusesQuery['routes']>['redeliveryStops']
>;
export type GetStopStatusDeliveryStop = Extract<
  GetStopStatusMyStop,
  { __typename?: 'Delivery' }
>;
export type GetStopStatusPickUpStop = Extract<
  GetStopStatusMyStop,
  { __typename?: 'PickUp' }
>;
export type StopStatusType = ArrayElement<
  ArrayElement<GGetAllStopStatusesQuery['routes']>['stops']
>;
export type GetStopStatusOrder =
  | ArrayElement<GetStopStatusDeliveryStop['orders']>
  | ArrayElement<GetStopStatusPickUpStop['orders']>;

export type GetAllStopStatusesRoute = ArrayElement<
  GGetAllStopStatusesQuery['routes']
>;

// GGetPrimaryStopStatusesQuery
export type GetPrimaryStopStatuses = ArrayElement<
  GGetPrimaryStopStatusesQuery['routes']
>;

// GGetRouteIssuesQuery
export type GetRouteIssuesMyStop = ArrayElement<
  GGetRouteIssuesQuery['route']['stops']
>;
export type GetRouteIssuesDeliveryStop = Extract<
  GetRouteIssuesMyStop,
  { __typename?: 'Delivery' }
>;
export type GetRouteIssuesOrder = ArrayElement<
  GetRouteIssuesDeliveryStop['orders']
>;
export type GetRouteIssuesOrderLine = ArrayElement<
  GetRouteIssuesOrder['orderLines']
>;
export type GetRouteIssuesOrderLineIssue = ArrayElement<
  GetRouteIssuesOrder['issues']
>;

// Compatibility aliases for shared route-details helpers.
export type GetRouteStopsDeliveryTimeRoute = GGetRouteDetailsQuery['route'];
export type GetRouteStopsDeliveryTimeStop = ArrayElement<
  GetRouteStopsDeliveryTimeRoute['stops']
>;
export type GetRouteStopsDeliveryTimeDelivery = Extract<
  GetRouteStopsDeliveryTimeStop,
  { __typename?: 'Delivery' }
>;
export type GetRouteStopsDeliveryTimePickUp = Extract<
  GetRouteStopsDeliveryTimeStop,
  { __typename?: 'PickUp' }
>;
export type GetRouteStopsDeliveryOrder = ArrayElement<
  GetRouteStopsDeliveryTimeDelivery['orders']
>;
export type GetRouteStopsDeliveryTimeRedeliveryStop = ArrayElement<
  GetRouteStopsDeliveryTimeRoute['redeliveryStops']
>;
export type GetRouteStopsRedeliveryOrder = ArrayElement<
  GetRouteStopsDeliveryTimeRedeliveryStop['orders']
>;

// GGetLocationsQuery
export type GetLocation = ArrayElement<
  GGetLocationsQuery['customerLocations']['nodes']
>;
export type GetLocationCohort = ArrayElement<
  GGetCohortsQuery['routingImportPlan']['cohorts']
>;

// GGetWarehouseOrderCutoffTimeQuery
export type GetWarehouseOrderCutoffTimeQuery = Extract<
  GGetWarehouseOrderCutOffTimeQuery['stop'],
  { __typename?: 'Delivery' }
>;

// GGetPickingLabelDataQuery
export type GetPickingLabelDataRoute = ArrayElement<
  GGetPickingLabelDataQuery['routes']
>;

// GGetFulfillmentDeltasPerRouteQuery
export type GetFulfillmentDelivery = Extract<
  ArrayElement<GGetFulfillmentDeltasPerRouteQuery['route']['stops']>,
  { __typename?: 'Delivery' }
>;

export type GetFulfillmentOrder = ArrayElement<
  GetFulfillmentDelivery['orders']
>;

// GGetFulfillmentDeltasPerRouteQuery
export type GetItemsDelivery = Extract<
  ArrayElement<GGetItemsPerRouteQuery['route']['stops']>,
  { __typename?: 'Delivery' }
>;

export type GetItemsOrder = ArrayElement<GetItemsDelivery['orders']>;

// refrigeration state
export enum RefrigerationState {
  refrigerated = 'refrigerated',
  frozen = 'frozen_state',
}

// GGetWarehouseDeliveryInfoQuery
export type GetWarehouseDeliveryInfoRoute = ArrayElement<
  GGetWarehouseDeliveryInfoQuery['routes']
>;
export type GetWarehouseDeliveryInfoStop = ArrayElement<
  GetWarehouseDeliveryInfoRoute['stops']
>;
export type GetWarehouseRedeliveryInfoStop = ArrayElement<
  GetWarehouseDeliveryInfoRoute['redeliveryStops']
>;
export type GetWarehouseDeliveryInfoDelivery = Extract<
  GetWarehouseDeliveryInfoStop,
  { __typename?: 'Delivery' }
>;
export type GetWarehouseDeliveryInfoPickUp = Extract<
  GetWarehouseDeliveryInfoStop,
  { __typename?: 'PickUp' }
>;
export type GetWarehouseDeliveryInfoOrder = ArrayElement<
  GetWarehouseDeliveryInfoDelivery['orders']
>;
export type GetWarehouseDeliveryInfoOrderLine = ArrayElement<
  GetWarehouseDeliveryInfoOrder['orderLines']
>;
export type GetWarehouseDeliveryItem =
  GetWarehouseDeliveryInfoOrderLine['sku']['item'];

// GGetOrdersQuery
export type GetOrders = GGetOrdersQuery['orders'];
export type GetOrderNode = ArrayElement<GGetOrdersQuery['orders']['nodes']>;

// GGetOrderDetailsQuery
export type OrderDetails = Extract<
  GGetOrderDetailsQuery['order'],
  { __typename?: 'Order' }
>;
export type LocationOrderDetails = Extract<
  OrderDetails['location'],
  { __typename?: 'Location' }
>;
export type AddressOrderDetails = Extract<
  LocationOrderDetails['address'],
  { __typename?: 'Address' }
>;

// GGetPackingListQuery
export type GetPackingListRoute = ArrayElement<GGetPackingListQuery['routes']>;
export type GetPackingListDelivery = Extract<
  ArrayElement<GetPackingListRoute['stops']>,
  { __typename?: 'Delivery' }
>;
export type GetPackingListOrder = ArrayElement<
  GetPackingListDelivery['orders']
>;
export type GetPackingListOrderLine = ArrayElement<
  GetPackingListOrder['orderLines']
>;

// GGetDeliverySheetsQuery
export type GetDeliverySheetsRoute = ArrayElement<
  GGetDeliverySheetsQuery['routes']
>;
export type GetDeliverySheetsDelivery = Extract<
  ArrayElement<GetDeliverySheetsRoute['stops']>,
  { __typename?: 'Delivery' }
>;
export type GetDeliverySheetsOrder = ArrayElement<
  GetDeliverySheetsDelivery['orders']
>;
export type GetDeliverySheetsOrderLine = ArrayElement<
  GetDeliverySheetsOrder['orderLines']
>;
export type GetDeliverySheetsOrderFee = ArrayElement<
  GetDeliverySheetsOrder['appliedFees']
>;

// GGetBulkItemsQuery
export type BulkItem = ArrayElement<
  GGetBulkItemsQuery['warehouseBulkPickedItems']['nodes']
>;

// GGetDeliveryPhotosQuery
export type GetDeliveryPhotoStop = Extract<
  GGetDeliveryPhotosQuery['stop'],
  { __typename?: 'Delivery' }
>;

// GGetRoutingImportPlanCutOff
export type RoutingImportCohort = ArrayElement<
  GGetRoutingImportPlanCutOffQuery['routingImportPlan']['cohorts']
>;

// One fleet shift's truck count for the deliveries Truck Count section. A null warehouseShiftId is the
// pooled bucket ("Pooled stops"); shiftType/startTime/endTime are null there too.
export type ShiftTruckCount = ArrayElement<
  GGetRoutingImportPlanCutOffQuery['routingImportPlan']['shiftTruckCounts']
>;

// GGetRouteDetailsQuery
export type GetRouteDetailsRoute = GGetRouteDetailsQuery['route'];
export type GetRouteDetailsStop = ArrayElement<GetRouteDetailsRoute['stops']>;
export type GetRouteDetailsDelivery = Extract<
  GetRouteDetailsStop,
  { __typename?: 'Delivery' }
>;
export type GetRouteDetailsPickUp = Extract<
  GetRouteDetailsStop,
  { __typename?: 'PickUp' }
>;
export type GetRouteDetailsRedelivery = Extract<
  GetRouteDetailsStop,
  { __typename?: 'Redelivery' }
>;
export type GetRouteDetailsDeliveryOrder = ArrayElement<
  GetRouteDetailsDelivery['orders']
>;
// One enter/exit visit to a stop (post-completion revisits included). Same
// shape on Delivery and Redelivery, so derive it once from Delivery.
export type GetRouteDetailsStopVisit = ArrayElement<
  GetRouteDetailsDelivery['visits']
>;
export type GetRouteDetailsRedeliveryStop = ArrayElement<
  GetRouteDetailsRoute['redeliveryStops']
>;
export type GetRouteDetailsRedeliveryOrder = ArrayElement<
  GetRouteDetailsRedeliveryStop['orders']
>;
export type GetRouteDetailsRoutingEstimates = NonNullable<
  GetRouteDetailsRoute['routingEstimates']
>;
export type GetRouteDetailsDeliveryRoutingEstimates = NonNullable<
  GetRouteDetailsDelivery['routingEstimates']
>;

// GGetWarehouseDeliveryDetailsQuery
export type GetWarehouseDeliveryDetailsRoute = ArrayElement<
  GGetWarehouseDeliveryDetailsQuery['routes']
>;
export type GetWarehouseDeliveryMapDetailsRoute = ArrayElement<
  GGetWarehouseDeliveryMapDetailsQuery['routes']
>;
export type GetWarehouseDriverPerformanceDetailsRoute = ArrayElement<
  GGetWarehouseDriverPerformanceDetailsQuery['routes']
>;

export type GetScenarioMapStopsRoute = ArrayElement<
  GGetScenarioMapStopsQuery['routes']
>;

export type RoutificRunSummary = ArrayElement<
  GGetRoutingImportPlanComparisonQuery['routingImportPlan']['scenarios']
>;
export type RouteSummary = ArrayElement<
  NonNullable<RoutificRunSummary['routes']>
>;
export type AppliedRoutificRun = NonNullable<
  GGetRoutingImportPlanComparisonQuery['routingImportPlan']['appliedRoutificRun']
>;
export type ManualRoutificRun = ArrayElement<
  GGetRoutingImportPlanComparisonQuery['routingImportPlan']['manualRoutificRuns']
>;
export type ActualRoutesSummary = NonNullable<
  GGetRoutingImportPlanComparisonQuery['routingImportPlan']['actualRoutesSummary']
>;
export type EtasStatus =
  GGetWarehouseRoutingImportPlanComparisonQuery['routingImportPlan']['etasStatus'];

type WarehouseRoutingImportPlan =
  GGetWarehouseRoutingImportPlanComparisonQuery['routingImportPlan'];
export type WarehouseAppliedRoutificRun = NonNullable<
  WarehouseRoutingImportPlan['appliedRoutificRun']
>;
export type WarehouseManualRoutificRun = ArrayElement<
  WarehouseRoutingImportPlan['manualRoutificRuns']
>;
export type WarehouseActualRoutesSummary = NonNullable<
  WarehouseRoutingImportPlan['actualRoutesSummary']
>;

// Additional Tasks (sc-64713). Hand-written until the LDS `tasks` field is on the
// stage schema and codegen (generate:types:stage-1) emits the G-types; the
// mutations follow the driver-reminder precedent and stay untyped for now.
export type RouteStopTaskStatus = 'created' | 'complete' | 'incomplete';

export type RouteStopTask = {
  id: string;
  details: string;
  status: RouteStopTaskStatus;
  resolvedAt: string | null;
  driverNote: string | null;
  createdAt: string;
};

export type GetStopTasksQuery = {
  stop: {
    __typename?: 'Delivery' | 'PickUp' | 'Redelivery';
    id?: string;
    tasks?: Array<RouteStopTask> | null;
  };
};
